/**
 * 顶栏工具栏 —— 见 SPEC.md §5.4
 */
import { useEffect, useState } from "react";
import {
  Settings,
  Trash2,
  Undo2,
  Download,
  Camera,
  ClipboardCopy,
  LayoutGrid,
  Atom,
  Sigma,
  MonitorDown
} from "lucide-react";
import { useAppStore } from "../store/useAppStore";
import { exportGGB, exportPNG } from "../lib/ggbBridge";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface Props {
  onOpenSettings: () => void;
  onOpenGallery: () => void;
}

export function Toolbar({ onOpenSettings, onOpenGallery }: Props) {
  const ggbApi = useAppStore(s => s.ggbApi);
  const domain = useAppStore(s => s.domain);
  const setDomain = useAppStore(s => s.setDomain);
  const messages = useAppStore(s => s.messages);
  const clearMessages = useAppStore(s => s.clearMessages);
  const undoLastTurn = useAppStore(s => s.undoLastTurn);
  const [domainToast, setDomainToast] = useState<string | null>(null);

  const switchDomain = (d: typeof domain) => {
    if (d === domain) return;
    setDomain(d);
    const msg =
      d === "physics"
        ? "已切换 ⚛ 物理模式 — 启用矢量箭头、物理常量、受力分析等专项支持"
        : "已切换 📐 数学模式 — 通用几何、函数、圆锥曲线、数列";
    setDomainToast(msg);
    setTimeout(() => setDomainToast(null), 2500);
  };

  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const onClear = () => {
    if (!ggbApi) return;
    if (confirm("清空画布与聊天历史？")) {
      ggbApi.newConstruction();
      clearMessages();
    }
  };

  const onUndo = () => {
    if (!ggbApi || messages.length === 0) return;
    if (!messages.some(m => m.role === "assistant")) return;
    undoLastTurn();
    ggbApi.newConstruction();
    // 重放剩余 commands
    const after = useAppStore.getState().messages;
    for (const m of after) {
      if (m.role === "assistant") {
        for (const cmd of m.payload.commands) {
          if (cmd.op === "eval") ggbApi.evalCommand(cmd.cmd);
        }
      }
    }
  };

  const onExportGGB = async () => {
    if (!ggbApi) return;
    const data = await exportGGB(ggbApi);
    const blob = await (await fetch(`data:application/octet-stream;base64,${data}`)).blob();
    triggerDownload(blob, `aiggb-${Date.now()}.ggb`);
  };

  const onScreenshot = async () => {
    if (!ggbApi) return;
    const dataUrl = exportPNG(ggbApi);
    const blob = await (await fetch(dataUrl)).blob();
    triggerDownload(blob, `aiggb-${Date.now()}.png`);
  };

  const onCopyScript = async () => {
    const cmds = messages.flatMap(m =>
      m.role === "assistant"
        ? m.payload.commands.filter(c => c.op === "eval").map(c => (c as { cmd: string }).cmd)
        : []
    );
    await navigator.clipboard.writeText(cmds.join("\n"));
    alert(`已复制 ${cmds.length} 条 GGB 命令到剪贴板`);
  };

  const onInstall = async () => {
    if (!installEvt) {
      alert("当前浏览器或环境不支持自动安装。\n你也可以在浏览器菜单中选择 “安装 AiGGB” 或 “添加到主屏幕”。");
      return;
    }
    await installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    if (outcome === "accepted") setInstallEvt(null);
  };

  return (
    <header className="toolbar">
      <div className="toolbar-brand">
        <span className="logo">∮</span>
        <span className="title">AiGGB</span>
        <span className="subtitle">AI × GeoGebra</span>
      </div>

      {domainToast && <div className="domain-toast">{domainToast}</div>}

      <div className="toolbar-actions">
        <div className="domain-switch">
          <button
            className={domain === "general" ? "active" : ""}
            onClick={() => switchDomain("general")}
            title="数学模式：几何、函数、圆锥曲线、数列、3D 几何体"
          >
            <Sigma size={14} /> 数学
          </button>
          <button
            className={domain === "physics" ? "active" : ""}
            onClick={() => switchDomain("physics")}
            title="物理域：矢量、常量、受力分析、电场"
          >
            <Atom size={14} /> 物理
          </button>
        </div>

        <button onClick={onOpenGallery} title="物理 / 数学模板">
          <LayoutGrid size={16} /> 模板
        </button>
        <button onClick={onUndo} disabled={messages.length === 0} title="撤销上一轮 AI 命令">
          <Undo2 size={16} />
        </button>
        <button onClick={onClear} title="清空画布与聊天">
          <Trash2 size={16} />
        </button>
        <button onClick={onExportGGB} disabled={!ggbApi} title="导出 .ggb 文件">
          <Download size={16} />
        </button>
        <button onClick={onScreenshot} disabled={!ggbApi} title="截图 PNG">
          <Camera size={16} />
        </button>
        <button onClick={onCopyScript} title="复制 GGB 脚本">
          <ClipboardCopy size={16} />
        </button>
        {installEvt && (
          <button onClick={onInstall} className="install" title="安装到桌面">
            <MonitorDown size={16} /> 安装
          </button>
        )}
        <button onClick={onOpenSettings} title="API 设置">
          <Settings size={16} />
        </button>
      </div>
    </header>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
